import { useCallback, useEffect } from 'react';

export const useProductDevelopmentHelpers = ({
  setCurrentPage,
  setRowsPerPage,
  setOpen,
  setSelectedProject,
  setOpenedProjectTab,
  open,
  setLastSyncTime,
  projects,
  totalProjects,
  rowsPerPage,
  currentPage,
  setSelectedCampaigns,
  setSelectedStages,
  setSelectedBatches,
  setSelectedOperations,
  loadDashboardData,
}) => {
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1);
  }, [setRowsPerPage, setCurrentPage]);

  const toggleExpand = useCallback((id, project) => {
    setOpen((prevState) => {
      const isCurrentlyOpen = prevState[id];
      const newState = {
        ...prevState,
        [id]: !isCurrentlyOpen,
      };
      
      setSelectedProject(isCurrentlyOpen ? null : id);
      
      if (!isCurrentlyOpen && project) {
        // Auto-populate default values from projectDetails (first values in order)
        const projectDetails = project.projectDetails;
        
        if (projectDetails && projectDetails.campaigns && projectDetails.campaigns.length > 0) {
          // Get first campaign (default)
          const firstCampaign = projectDetails.campaigns[0];
          setSelectedCampaigns((prev) => ({
            ...prev,
            [id]: firstCampaign.campaignId,
          }));
          
          // Get first stage of first campaign (default)
          if (firstCampaign.stages && firstCampaign.stages.length > 0) {
            const firstStage = firstCampaign.stages[0];
            setSelectedStages((prev) => ({
              ...prev,
              [id]: firstStage.stageId,
            }));
            
            // Get first batch of first stage (default)
            if (firstStage.batches && firstStage.batches.length > 0) {
              const firstBatch = firstStage.batches[0];
              setSelectedBatches((prev) => ({
                ...prev,
                [id]: {
                  batchNumber: firstBatch.batchNumber,
                  batchId: firstBatch.batchId,
                },
              }));
              
              // Always default to 'All' for operation
              setSelectedOperations((prev) => ({
                ...prev,
                [id]: 'All',
              }));
            }
          }
        }
        
        setOpenedProjectTab((prevTabState) => ({
          ...prevTabState,
          [id]: 0,
        }));
      }
      
      return newState;
    });
  }, [setOpen, setSelectedProject, setOpenedProjectTab, setSelectedCampaigns, setSelectedStages, setSelectedBatches, setSelectedOperations]);

  const handleOpenProjectTab = useCallback((projectId, tabIndex) => {
    setOpenedProjectTab((prevState) => ({
      ...prevState,
      [projectId]: tabIndex,
    }));
  }, [setOpenedProjectTab]);

  const handleRefresh = useCallback(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Server-side pagination: the API already returns just the current page (and
  // its total record count via totalProjects). Page count is derived from that
  // total; the rows are used as-is — slicing again client-side would blank out
  // every page after the first (each page only holds `rowsPerPage` rows).
  const recordCount =
    typeof totalProjects === 'number' && totalProjects > 0
      ? totalProjects
      : projects.length;
  const totalPages = Math.max(1, Math.ceil(recordCount / rowsPerPage));
  const paginatedProjects = projects;

  // The page index must never run past the last page — a filter/search that
  // shrinks the result set would otherwise leave the pager on a page that no
  // longer exists (and request it from the API on the next reload).
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  return {
    handlePageChange,
    handleRowsPerPageChange,
    toggleExpand,
    handleOpenProjectTab,
    handleRefresh,
    totalPages,
    paginatedProjects,
  };
};

